import React, { useState, useMemo, useEffect } from "react";
import apiClient from "../utils/apiClient";
import { X, Search, Check } from "lucide-react";
import server from "../utils/serverConfig";
import toast from "react-hot-toast";

const AlpacaBulkOrderModal = ({ isOpen, onClose, users, onSuccess }) => {
    const [formData, setFormData] = useState({
        symbol: "",
        strategy: "",
        baseQty: "",
        side: "1", // "1" for Buy, "-1" for Sell
        orderType: "MARKET",
        limitPrice: "0",
        stopPrice: "0",
        validity: "DAY", // DAY or GTC
        entry: "0",
        calculationMode: "MULTIPLIER", // MULTIPLIER or EXACT
        exactQty: "",
    });

    const [searchQuery, setSearchQuery] = useState("");
    const [selectedClients, setSelectedClients] = useState(new Set());
    const [submitting, setSubmitting] = useState(false);

    // Reset selection when modal opens
    useEffect(() => {
        if (isOpen) {
            setSelectedClients(new Set());
        }
    }, [isOpen]);

    // Filter valid Alpaca clients
    const eligibleClients = useMemo(() => {
        if (!users) return [];

        return users.filter(u => {
            if (!u) return false;
            const broker = (u.broker || "").toLowerCase();
            const status = (u.status || "").toLowerCase();
            return broker === "alpaca" && status === "active";
        }).map(u => {
            const multiplier = parseFloat(u.multiplier || 0) || 1;
            return {
                ...u,
                currentMultiplier: multiplier
            };
        });
    }, [users]);

    const filteredClients = useMemo(() => {
        if (!searchQuery.trim()) return eligibleClients;
        const q = searchQuery.toLowerCase();
        return eligibleClients.filter(c =>
            (c.name || "").toLowerCase().includes(q) ||
            (c.client_id || "").toLowerCase().includes(q)
        );
    }, [eligibleClients, searchQuery]);

    const handleSelectAll = () => {
        if (selectedClients.size === filteredClients.length) {
            setSelectedClients(new Set());
        } else {
            const newSet = new Set(filteredClients.map(c => c.client_id));
            setSelectedClients(newSet);
        }
    };

    const handleToggleClient = (clientId) => {
        const next = new Set(selectedClients);
        if (next.has(clientId)) {
            next.delete(clientId);
        } else {
            next.add(clientId);
        }
        setSelectedClients(next);
    };

    const handleFieldChange = (field, val) => {
        setFormData(prev => {
            const next = { ...prev, [field]: val };
            if (field === "orderType") {
                if (val === "MARKET") {
                    next.limitPrice = "0";
                    next.stopPrice = "0";
                } else if (val === "LIMIT") {
                    next.stopPrice = "0";
                } else if (val === "STOP") {
                    next.limitPrice = "0";
                }
            }
            return next;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (selectedClients.size === 0) {
            toast.error("Please select at least one client");
            return;
        }
        if (!formData.symbol || !formData.strategy) {
            toast.error("Please fill required fields (Symbol and Strategy)");
            return;
        }

        if (formData.calculationMode === "MULTIPLIER" && !formData.baseQty) {
            toast.error("Please specify Base Quantity");
            return;
        }

        if (formData.calculationMode === "EXACT" && !formData.exactQty) {
            toast.error("Please specify Exact Quantity");
            return;
        }

        setSubmitting(true);
        try {
            const orderPayload = Array.from(selectedClients).map(clientId => {
                const client = eligibleClients.find(c => c.client_id === clientId);
                let qty;
                if (formData.calculationMode === "MULTIPLIER") {
                    qty = Number(formData.baseQty) * (client?.currentMultiplier || 1);
                } else {
                    qty = Number(formData.exactQty);
                }

                return {
                    client_id: clientId,
                    symbol: formData.symbol.trim().toUpperCase(),
                    strategy: formData.strategy.trim(),
                    qty: qty,
                    side: Number(formData.side),
                    orderType: formData.orderType,
                    limitPrice: Number(formData.limitPrice),
                    stopPrice: Number(formData.stopPrice),
                    validity: formData.validity,
                    entry: Number(formData.entry) || 0,
                    broker: "alpaca"
                };
            });

            // Flatten nested client order_resp structure for ExitSuccessModal
            const flattenResponse = (apiData) => {
                if (!apiData || !Array.isArray(apiData)) return [];
                const flattened = [];
                apiData.forEach(clientEntry => {
                    if (clientEntry.order_resp && Array.isArray(clientEntry.order_resp)) {
                        clientEntry.order_resp.forEach(order => {
                            const isFailed = order.status === "failed" || order.status === "Rejected";
                            const orderId = isFailed ? null : (order.response?.data?.order_id || order.response?.id || order.id || order.order_id || order.orderId || "PLACED");
                            flattened.push({
                                clientId: clientEntry.client_id || order.clientId,
                                symbol: order.symbol || "N/A",
                                qty: order.qty || 0,
                                status: isFailed ? "Rejected" : "PENDING",
                                rejectReason: order.response?.message || order.response?.error || order.message || "",
                                orderId: orderId,
                                broker: "alpaca"
                            });
                        });
                    } else if (clientEntry.orderId || clientEntry.orderid || clientEntry.order_id) {
                        flattened.push({
                            ...clientEntry,
                            orderId: clientEntry.orderId || clientEntry.orderid || clientEntry.order_id,
                            clientId: clientEntry.clientId || clientEntry.client_id,
                            broker: clientEntry.broker || "alpaca"
                        });
                    }
                });
                return flattened;
            };

            // Call godseye manual order placement endpoint
            const res = await apiClient.post(`${server.godseye.baseUrl}order-placement`, orderPayload);
            const apiResp = res.data;

            if (apiResp.status === 200 || apiResp.status === 207) {
                toast.success(apiResp.message || "Manual Alpaca orders placed successfully!");
                const flattenedOrders = flattenResponse(apiResp.data);
                if (onSuccess) onSuccess(flattenedOrders);
                onClose();
            } else {
                toast.error(apiResp.message || "Manual order placement returned issues.");
            }
        } catch (err) {
            console.error("Manual order placement failed:", err);
            toast.error("Placement failed: " + (err.response?.data?.detail || err.message));
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] backdrop-blur-sm p-4">
            <div className="bg-[#1a1d29] rounded-xl border border-gray-700 w-full max-w-5xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center bg-[#14161f]">
                    <div>
                        <h2 className="text-white text-xl font-bold flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-[#FCD72B]"></span>
                            Alpaca Order Placement
                        </h2>
                        <p className="text-gray-400 text-xs mt-1">Place manual orders for multiple Alpaca clients at once</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-full text-gray-400 transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="flex-1 overflow-auto p-6 flex flex-col lg:flex-row gap-6">
                    {/* Form Side */}
                    <form onSubmit={handleSubmit} className="lg:w-1/2 space-y-4 flex flex-col justify-between">
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Symbol (Options/Equity)*</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="e.g. SPY, AAPL260807C00220000"
                                        value={formData.symbol}
                                        onChange={(e) => handleFieldChange("symbol", e.target.value)}
                                        className="w-full bg-[#0f1116] border border-gray-800 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-700"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Strategy Name*</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="e.g. simple, straddle"
                                        value={formData.strategy}
                                        onChange={(e) => handleFieldChange("strategy", e.target.value)}
                                        className="w-full bg-[#0f1116] border border-gray-800 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-700"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Side</label>
                                    <select
                                        value={formData.side}
                                        onChange={(e) => handleFieldChange("side", e.target.value)}
                                        className="w-full bg-[#0f1116] border border-gray-800 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-700"
                                    >
                                        <option value="1">BUY</option>
                                        <option value="-1">SELL</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Order Type</label>
                                    <select
                                        value={formData.orderType}
                                        onChange={(e) => handleFieldChange("orderType", e.target.value)}
                                        className="w-full bg-[#0f1116] border border-gray-800 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-700"
                                    >
                                        <option value="MARKET">MARKET</option>
                                        <option value="LIMIT">LIMIT</option>
                                        <option value="STOP">STOP</option>
                                        <option value="STOP-LIMIT">STOP-LIMIT</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Time In Force</label>
                                    <select
                                        value={formData.validity}
                                        onChange={(e) => handleFieldChange("validity", e.target.value)}
                                        className="w-full bg-[#0f1116] border border-gray-800 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-700"
                                    >
                                        <option value="DAY">DAY</option>
                                        <option value="GTC">GTC</option>
                                    </select>
                                </div>
                            </div>

                            {/* Prices based on order type */}
                            {formData.orderType !== "MARKET" && (
                                <div className="grid grid-cols-2 gap-4">
                                    {(formData.orderType === "LIMIT" || formData.orderType === "STOP-LIMIT") && (
                                        <div>
                                            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Limit Price</label>
                                            <input
                                                type="number"
                                                step="any"
                                                required
                                                value={formData.limitPrice}
                                                onChange={(e) => handleFieldChange("limitPrice", e.target.value)}
                                                className="w-full bg-[#0f1116] border border-gray-800 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-700"
                                            />
                                        </div>
                                    )}
                                    {(formData.orderType === "STOP" || formData.orderType === "STOP-LIMIT") && (
                                        <div>
                                            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Stop Price</label>
                                            <input
                                                type="number"
                                                step="any"
                                                required
                                                value={formData.stopPrice}
                                                onChange={(e) => handleFieldChange("stopPrice", e.target.value)}
                                                className="w-full bg-[#0f1116] border border-gray-800 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-700"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Qty calculation mode */}
                            <div className="bg-[#14161f] border border-gray-800 p-4 rounded-lg space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Quantity Calculation</label>
                                    <div className="flex gap-4">
                                        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-300">
                                            <input
                                                type="radio"
                                                name="calculationMode"
                                                checked={formData.calculationMode === "MULTIPLIER"}
                                                onChange={() => handleFieldChange("calculationMode", "MULTIPLIER")}
                                                className="accent-blue-500"
                                            />
                                            Base Qty * Multiplier
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-300">
                                            <input
                                                type="radio"
                                                name="calculationMode"
                                                checked={formData.calculationMode === "EXACT"}
                                                onChange={() => handleFieldChange("calculationMode", "EXACT")}
                                                className="accent-blue-500"
                                            />
                                            Exact Quantity
                                        </label>
                                    </div>
                                </div>

                                {formData.calculationMode === "MULTIPLIER" ? (
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Base Quantity</label>
                                        <input
                                            type="number"
                                            placeholder="Base quantity..."
                                            value={formData.baseQty}
                                            onChange={(e) => handleFieldChange("baseQty", e.target.value)}
                                            className="w-full bg-[#0f1116] border border-gray-800 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-700"
                                        />
                                    </div>
                                ) : (
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Exact Qty per client</label>
                                        <input
                                            type="number"
                                            placeholder="Exact quantity..."
                                            value={formData.exactQty}
                                            onChange={(e) => handleFieldChange("exactQty", e.target.value)}
                                            className="w-full bg-[#0f1116] border border-gray-800 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-700"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="pt-4 flex gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 px-4 py-2 border border-gray-800 hover:bg-gray-800/40 text-gray-400 rounded text-sm font-bold uppercase transition duration-150"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={submitting}
                                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-600/50 text-white rounded text-sm font-bold uppercase transition duration-150"
                            >
                                {submitting ? "Placing..." : `Submit Orders (${selectedClients.size})`}
                            </button>
                        </div>
                    </form>

                    {/* Clients List Side */}
                    <div className="lg:w-1/2 flex flex-col border-l border-gray-800 pl-6 overflow-hidden">
                        <div className="flex justify-between items-center mb-4">
                            <span className="text-sm font-bold text-white">Select Clients</span>
                            <button
                                type="button"
                                onClick={handleSelectAll}
                                className="text-xs text-blue-400 hover:text-blue-300 font-semibold"
                            >
                                {selectedClients.size === filteredClients.length ? "Deselect All" : "Select All"}
                            </button>
                        </div>

                        {/* Search client input */}
                        <div className="relative mb-3">
                            <input
                                type="text"
                                placeholder="Search client name or ID..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full bg-[#0f1116] border border-gray-800 rounded px-3 py-2 text-xs text-gray-300 focus:outline-none focus:border-gray-700"
                            />
                        </div>

                        {/* Clients List */}
                        <div className="flex-1 overflow-y-auto space-y-2 sleek-scrollbar pr-2 min-h-[300px]">
                            {filteredClients.length === 0 ? (
                                <p className="text-gray-500 text-sm text-center py-8">No active Alpaca clients found</p>
                            ) : (
                                filteredClients.map(c => {
                                    const isSelected = selectedClients.has(c.client_id);
                                    let previewQty = "-";
                                    if (formData.calculationMode === "MULTIPLIER" && formData.baseQty) {
                                        previewQty = Number(formData.baseQty) * (c.currentMultiplier || 1);
                                    } else if (formData.calculationMode === "EXACT" && formData.exactQty) {
                                        previewQty = Number(formData.exactQty);
                                    }

                                    return (
                                        <div
                                            key={c.client_id}
                                            onClick={() => handleToggleClient(c.client_id)}
                                            className={`p-3 rounded-lg border cursor-pointer transition flex items-center justify-between ${
                                                isSelected 
                                                    ? "bg-blue-600/10 border-blue-600/40 text-white" 
                                                    : "bg-[#0f1116] border-gray-800 hover:border-gray-700 text-gray-400"
                                            }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`w-4 h-4 rounded border flex items-center justify-center transition ${
                                                    isSelected ? "bg-blue-600 border-blue-600" : "border-gray-800"
                                                }`}>
                                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                                </div>
                                                <div>
                                                    <span className="font-bold text-sm block">{c.name || c.client_id}</span>
                                                    <span className="text-[10px] text-gray-500 block font-mono">{c.client_id} (Mul: {c.currentMultiplier})</span>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <span className="text-xs font-semibold block uppercase text-gray-500">Qty</span>
                                                <span className="text-sm font-bold font-mono text-gray-300">{previewQty}</span>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AlpacaBulkOrderModal;
